import { useEffect, useState, useMemo, useCallback } from "react";
import { message, Spin } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getCreditLimitRequests,
  rejectCreditLimitRequest,
  reopenCreditLimitRequest,
  getBusEnt,
} from "../../../redux/slices/creditLimitSlice";
import { count } from "../../../redux/slices/dealsSlice";
import { count as allCount } from "../../../redux/slices/AuthSlice";
import { getCreditLimitRequestsColumns } from "./getCreditLimitRequestsColumns";
import TableHeader from "../../../components/CustomTable/TableHeader";
import CustomTable from "../../../components/CustomTable/CustomTable";
import CustomModal from "../../../components/CustomModal/CustomModal";
import ReasonForm from "../../../components/CustomForms/ReasonForm";
import ApproveEntBus from "./ApproveEntBus";
import CreditLimitActivityLog from "./CreditLimitActivityLog";
import {
  FORM_MODAL_WIDTHS,
  REASON_MODAL_CONFIG,
} from "../../../constants/constants";
import useSearchableTable from "../../../hooks/useSearchableTable";
import useModuleContext from "../../../hooks/useModuleContext";
import {
  TABLE_COLUMN_DEFAULTS,
  buildTableScreenKey,
} from "../../../constants/tableColumnDefaults";

const REQUEST_STATUS_DISPLAY_NAMES = {
  ALL: "All Requests",
  PENDING: "Pending",
  Approved: "Approved",
  Rejected: "Rejected",
};

const CreditLimitRequestsTable = ({ selectedExistingStatus }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    requestsData,
    requestsLoading,
    requestRejectLoading,
    requestReopenLoading,
    requestsError,
  } = useSelector((state) => state.creditLimit);
  const { user } = useSelector((state) => state.auth);

  const DEFAULT_REQUEST_STATUSES = useMemo(() => ["PENDING", "Approved"], []);
  const {
    selectedStatus: statusFilter,
    handleStatusChange: setStatusFilterContext,
    searchText,
    handleSearchChange,
    pagination,
  } = useModuleContext({
    moduleKey: "creditLimitRequests",
    isMultiStatus: true,
    allowedStatusIds: ["ALL", "PENDING", "Approved", "Rejected"],
    defaultStatus: DEFAULT_REQUEST_STATUSES,
    paramPrefix: "req",
  });
  const [modal, setModal] = useState({ type: null, data: null });
  const [columnControl, setColumnControl] = useState(null);

  const userPrivileges = useMemo(() => {
    const raw = user?.privilege || [];
    return raw
      .map((p) => (typeof p === "string" ? p : p?.privilegeCode))
      .filter(Boolean);
  }, [user]);

  const closeModal = useCallback(() => setModal({ type: null, data: null }), []);
  const openModal = useCallback((type, record) => setModal({ type, data: record }), []);

  useEffect(() => {
    dispatch(getCreditLimitRequests());
  }, [dispatch]);

  const statusListWithCounts = useMemo(() => {
    const all = requestsData || [];
    const pendingCount = all.filter((r) => r.status === "Pending Credit Limit").length;
    const approvedCount = all.filter((r) => r.status === "Approved").length;
    const rejectedCount = all.filter((r) => r.status === "Rejected").length;

    return [
      { statusId: "ALL", count: all.length },
      { statusId: "PENDING", count: pendingCount },
      { statusId: "Approved", count: approvedCount },
      { statusId: "Rejected", count: rejectedCount },
    ];
  }, [requestsData]);

  const filteredByStatus = useMemo(() => {
    const all = requestsData || [];
    if (Array.isArray(statusFilter)) {
      if (statusFilter.length === 0) return [];
      if (statusFilter.includes("ALL")) return all;
      return all.filter((item) => {
        return statusFilter.some((sf) => {
          if (sf === "PENDING") return item.status === "Pending Credit Limit";
          return item.status === sf;
        });
      });
    }
    if (statusFilter === "ALL") return all;
    if (statusFilter === "PENDING") {
      return all.filter((item) => item.status === "Pending Credit Limit");
    }
    if (!statusFilter) return [];
    return all.filter((item) => item.status === statusFilter);
  }, [requestsData, statusFilter]);

  const { filteredData } =
    useSearchableTable(filteredByStatus, searchText, handleSearchChange);

  const handleStatusFilterChange = useCallback((newFilter) => {
    if (!Array.isArray(newFilter)) {
      setStatusFilterContext(newFilter);
      return;
    }
    const hadAll = Array.isArray(statusFilter) && statusFilter.includes("ALL");
    const hasAll = newFilter.includes("ALL");

    if (!hadAll && hasAll) {
      setStatusFilterContext(["ALL"]);
    } else if (hadAll && hasAll && newFilter.length > 1) {
      setStatusFilterContext(newFilter.filter((item) => item !== "ALL"));
    } else {
      setStatusFilterContext(newFilter);
    }
  }, [statusFilter, setStatusFilterContext]);

  const handleRejectSubmit = async (values) => {
    if (!modal.data?.requestId) return;

    const res = await dispatch(
      rejectCreditLimitRequest({
        requestId: modal.data.requestId,
        payload: {
          rejectionReason: values.reason?.trim(),
        },
      })
    );

    if (res?.meta?.requestStatus === "fulfilled") {
      message.success("Credit limit request rejected successfully.");
      closeModal();
      dispatch(getCreditLimitRequests());
      dispatch(allCount("ALL"));
    } else {
      const err =
        res?.payload?.message ||
        res?.payload?.error ||
        res?.payload ||
        "Failed to reject credit limit request";
      message.error(typeof err === "string" ? err : JSON.stringify(err));
    }
  };

  const handleReopenSubmit = async (values) => {
    if (!modal.data?.requestId) return;

    const res = await dispatch(
      reopenCreditLimitRequest({
        requestId: modal.data.requestId,
        payload: {
          reopenReason: values.reason?.trim(),
        },
      })
    );

    if (res?.meta?.requestStatus === "fulfilled") {
      message.success("Credit limit request reopened successfully.");
      closeModal();
      dispatch(getCreditLimitRequests());
      dispatch(allCount("ALL"));
    } else {
      const err =
        res?.payload?.message ||
        res?.payload?.error ||
        res?.payload ||
        "Failed to reopen credit limit request";
      message.error(typeof err === "string" ? err : JSON.stringify(err));
    }
  };

  const columns = useMemo(
    () =>
      getCreditLimitRequestsColumns({
        onApprove: (record) => openModal("approve", record),
        onReject: (record) => openModal("reject", record),
        onReopen: (record) => openModal("reopen", record),
        onNavigateEnterprise: () => navigate("/enterprise"),
        onViewActivityLog: (record) => openModal("activityLog", record),
        privileges: userPrivileges,
      }),
    [openModal, navigate, userPrivileges]
  );

  return (
    <Spin spinning={requestsLoading}>
      <TableHeader
        title="Requests"
        searchText={searchText}
        setSearchText={handleSearchChange}
        isDropdown={true}
        dropdownData={statusListWithCounts}
        selectedStatus={statusFilter}
        handleStatusChange={handleStatusFilterChange}
        statusDisplayNames={REQUEST_STATUS_DISPLAY_NAMES}
        columnControl={columnControl}
      />

      <CustomTable
        rowKey="requestId"
        columns={columns}
        dataSource={filteredData}
        loading={requestsLoading}
        pagination={pagination}
        screenKey={buildTableScreenKey("creditLimitRequests")}
        defaultColumnKeys={TABLE_COLUMN_DEFAULTS.creditLimitRequests}
        onColumnControlChange={setColumnControl}
      />

      {modal.type === "approve" && (
        <CustomModal
          title="Approve Credit Limit"
          open
          centered
          width={FORM_MODAL_WIDTHS}
          content={
            <ApproveEntBus
              data={modal.data}
              onClose={() => {
                closeModal();
                dispatch(getCreditLimitRequests());
                if (selectedExistingStatus) {
                  dispatch(getBusEnt(selectedExistingStatus));
                }
                dispatch(count("CREDITLIMIT"));
              }}
              status={selectedExistingStatus}
            />
          }
        />
      )}

      {modal.type === "reject" && (
        <CustomModal
          title={REASON_MODAL_CONFIG.reject?.title}
          open
          centered
          content={
            <ReasonForm
              label={REASON_MODAL_CONFIG.reject?.label}
              placeholder="Write a valid reason"
              btnName={REASON_MODAL_CONFIG.reject?.btnName}
              btnType="danger"
              submitLoading={requestRejectLoading}
              submitError={requestsError}
              onClose={closeModal}
              handleOnFormSubmit={handleRejectSubmit}
            />
          }
        />
      )}

      {modal.type === "reopen" && (
        <CustomModal
          title={REASON_MODAL_CONFIG.reopen?.title}
          open
          centered
          content={
            <ReasonForm
              label={REASON_MODAL_CONFIG.reopen?.label}
              placeholder="Write a valid reason"
              btnName={REASON_MODAL_CONFIG.reopen?.btnName}
              btnType="primary"
              submitLoading={requestReopenLoading}
              submitError={requestsError}
              onClose={closeModal}
              handleOnFormSubmit={handleReopenSubmit}
            />
          }
        />
      )}

      {modal.type === "activityLog" && (
        <CustomModal
          title={`Activity Log - ${modal.data?.enterpriseName || modal.data?.orgName || modal.data?.requestId || "Credit Limit Request"}`}
          open
          centered
          width={850}
          closeIcon={<CloseOutlined style={{ fontSize: 16 }} data-testid="modal-close-icon" />}
          onCancel={closeModal}
          content={
            <div style={{ maxHeight: "70vh", overflowY: "auto", padding: "8px 4px" }}>
              <CreditLimitActivityLog
                requestId={modal.data?.requestId}
                busEntId={modal.data?.busEntId}
                entName={modal.data?.enterpriseName}
                orgName={modal.data?.orgName}
                title={`Request Activity Log (${modal.data?.requestId})`}
                onClose={closeModal}
              />
            </div>
          }
        />
      )}
    </Spin>
  );
};

export default CreditLimitRequestsTable;
